import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, HStack, Spinner, Stack, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import { loadInstalledDrivers, removeInstalledDriver, type DriverCatalog, type LoadedDriver } from '@casa/driver-host';
import type { DriverDiscovery, DriverManifest } from '@casa/driver-sdk';
import type { Dashboard } from '@casa/model';
import { BUILT_IN_MANIFESTS } from '@casa/drivers/manifests';
import { readCachedDashboard } from '../data/dashboard.ts';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';
import { ChromeError, ChromeNote } from '../ui/chrome_page.tsx';
import { discoverSystems, type FoundSystem } from './discovery.ts';
import { ConnectionsGroup, DriverExplorer } from './driver_explorer.tsx';
import { DriverInstall, Hash, Permissions } from './driver_install.tsx';
import { buildProfile, loadDrivers } from './drivers.ts';

// The drivers, a part of the settings (#settings/drivers): the built-in drivers, the installed ones,
// and installing a driver from a folder, a zip file or a web address after the user has seen and
// accepted what it may reach (proposal §6.3, `DriverInstall`). The dashboard picks up a change when it is loaded again. In the settings' rows
// (look LK-8): a tap on a driver shows what it may reach under it. Above them, the shown dashboard's
// connections, each opening the driver explorer (dashboard-editor spec §6.1).

/** What sits under a chosen row: its details, set off from the rows. */
function Details({ children }: { children: ReactNode }) : ReactNode {
    return (
        <Stack gap="2" px="4" pb="3" pt="1" bg="bg.emphasized">
            {children}
        </Stack>
    );
}

function InstalledDriver({ driver, isChosen, onChoose, onRemove }: { driver: LoadedDriver; isChosen: boolean; onChoose: () => void; onRemove: () => void }) : ReactNode {
    const remove = (
        <Button variant="outline" size="sm" alignSelf="flex-start" onClick={onRemove}>
            Remove
        </Button>
    );

    if (!driver.ok) {
        return (
            <Stack gap="0">
                <SettingsRow icon="triangle-exclamation" color="#f87171" title={driver.id} detail="Does not load" isSelected={isChosen} opens={false} onClick={onChoose} />
                {isChosen && (
                    <Details>
                        <ChromeError text={`Does not load: ${driver.reason}`} />
                        {remove}
                    </Details>
                )}
            </Stack>
        );
    }

    const { record, manifest } = driver;

    return (
        <Stack gap="0">
            <SettingsRow icon="puzzle-piece" title={`${manifest.name} ${record.version}`} detail={record.id} isSelected={isChosen} opens={false} onClick={onChoose} />
            {isChosen && (
                <Details>
                    <Permissions permissions={record.permissions} />
                    <Text color="fg.muted" fontSize="sm">
                        Installed {new Date(record.consentedAt).toLocaleString()}
                    </Text>
                    <Hash sha256={record.sha256} />
                    {remove}
                </Details>
            )}
        </Stack>
    );
}

/** The systems of a driver's kind found on the LAN (M-8), looked for when its details open. */
function FoundOnNetwork({ shell, discovery }: { shell: Bridge; discovery: DriverDiscovery }) : ReactNode {
    const [found, setFound] = useState<FoundSystem[] | string>();

    useEffect(() => {
        let isCurrent = true;
        discoverSystems(shell, discovery).then(
            (systems) => isCurrent && setFound(systems),
            (error: unknown) => isCurrent && setFound(error instanceof Error ? error.message : String(error)),
        );

        return () => {
            isCurrent = false;
        };
    }, [shell, discovery]);

    if (found === undefined) {
        return (
            <HStack gap="2" color="fg.muted" fontSize="sm">
                <Spinner size="xs" />
                <Text>Looking on this network…</Text>
            </HStack>
        );
    }
    if (typeof found === 'string') {
        return <Text color="fg.muted" fontSize="sm">This device cannot look on the network: {found}</Text>;
    }

    return (
        <Text color="fg.muted" fontSize="sm">
            {found.length === 0 ? 'None found on this network.' : `On this network: ${found.map((system) => `${system.name} (${system.address})`).join(', ')}.`}
        </Text>
    );
}

function BuiltInDriver({ shell, manifest, isChosen, onChoose }: { shell: Bridge; manifest: DriverManifest; isChosen: boolean; onChoose: () => void }) : ReactNode {
    return (
        <Stack gap="0">
            <SettingsRow icon="puzzle-piece" title={manifest.name} detail={manifest.description ?? manifest.id} isSelected={isChosen} opens={false} onClick={onChoose} />
            {isChosen && (
                <Details>
                    <Text color="fg.muted" fontSize="sm">
                        {manifest.id}
                    </Text>
                    <Permissions permissions={manifest.permissions} />
                    {manifest.discovery ? <FoundOnNetwork shell={shell} discovery={manifest.discovery} /> : null}
                </Details>
            )}
        </Stack>
    );
}

export function DriversView({ shell }: { shell: Bridge }) : ReactNode {
    const profile = buildProfile();
    const [installed, setInstalled] = useState<LoadedDriver[]>([]);
    const [message, setMessage] = useState<string | undefined>();
    const [chosen, setChosen] = useState<string>();
    const toggle = (id: string) : (() => void) => () => setChosen(chosen === id ? undefined : id);

    const [dashboard, setDashboard] = useState<Dashboard>();
    const [catalog, setCatalog] = useState<DriverCatalog>();
    const [exploring, setExploring] = useState<string>();

    const reload = useCallback(async () : Promise<void> => {
        setInstalled(await loadInstalledDrivers(shell.storage));
        setCatalog(await loadDrivers(shell));
    }, [shell]);
    useEffect(() => {
        void reload();
        void readCachedDashboard(shell).then(setDashboard, () => setDashboard(undefined));
    }, [reload, shell]);

    const remove = async (id: string) : Promise<void> => {
        await removeInstalledDriver(shell.storage, id);
        setMessage(`Removed ${id}.`);
        await reload();
    };

    const connection = exploring === undefined ? undefined : dashboard?.connections[exploring];
    if (exploring !== undefined && connection && catalog) {
        const resolved = catalog.resolve(connection.driver);

        return (
            <Stack gap="4">
                <Button variant="ghost" size="sm" alignSelf="flex-start" color="blue.400" px="1" onClick={() => setExploring(undefined)}>
                    ‹ Drivers
                </Button>
                <Text fontSize="xl" fontWeight="semibold">
                    {exploring}
                    <Text as="span" color="fg.muted" fontWeight="normal">
                        {' '}
                        · {resolved.ok ? resolved.manifest.name : connection.driver}
                    </Text>
                </Text>
                {resolved.ok ? (
                    <DriverExplorer shell={shell} name={exploring} config={connection.config} driver={resolved} />
                ) : (
                    <ChromeError text={resolved.reason} />
                )}
            </Stack>
        );
    }

    const describeDriver = (driver: string) : string => {
        const resolved = catalog?.resolve(driver);

        return resolved?.ok ? resolved.manifest.name : driver;
    };

    return (
        <Stack gap="6">
            <ConnectionsGroup dashboard={dashboard} describeDriver={describeDriver} onOpen={setExploring} />
            {!profile.userDrivers ? (
                <SettingsGroup title="Installed drivers" note="This build does not run drivers installed by the user.">
                    <SettingsRow title={<Text as="span" color="fg.muted">None</Text>} />
                </SettingsGroup>
            ) : (
                <Stack gap="3">
                    <SettingsGroup title="Installed drivers">
                        {installed.length === 0 ? <SettingsRow title={<Text as="span" color="fg.muted">None</Text>} /> : null}
                        {installed.map((driver) => {
                            const id = driver.ok ? driver.record.id : driver.id;

                            return <InstalledDriver key={id} driver={driver} isChosen={chosen === id} onChoose={toggle(id)} onRemove={() => void remove(id)} />;
                        })}
                    </SettingsGroup>
                    <DriverInstall
                        shell={shell}
                        installed={installed}
                        onInstalled={(manifest) => {
                            setMessage(`Installed ${manifest.name} ${manifest.version}. Open the dashboard again to use it.`);
                            void reload();
                        }}
                    />
                    {message ? <ChromeNote status="success" text={message} /> : null}
                </Stack>
            )}
            <SettingsGroup title="Built-in drivers">
                {Object.values(BUILT_IN_MANIFESTS).map((manifest) => (
                    <BuiltInDriver key={manifest.id} shell={shell} manifest={manifest} isChosen={chosen === manifest.id} onChoose={toggle(manifest.id)} />
                ))}
            </SettingsGroup>
        </Stack>
    );
}
